/**
 * External dependencies
 */
import React from "react";
import ReactSelect from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";

/**
 * Error message
 */
interface ErrorMessageProps {
	title?: string;
	text?: string;
}

export const ErrorMessage = ({ title, text }: ErrorMessageProps) => {
	if (!title && !text) {
		return null;
	}

	return (
		<div className="em-block-service-feed__error-message">
			{!!title && <h3>{title}</h3>}
			{!!text && <p>{text}</p>}
		</div>
	);
};

/**
 * React Select configuration
 */
const AppReactSelect = (props: StateManagerProps) => {
	return (
		<ReactSelect
			styles={{
				valueContainer: (provided) => ({
					...provided,
					paddingLeft: 8,
					paddingRight: 8,
				}),
				placeholder: (provided) => ({
					...provided,
					color: "var(--color-gray-500)",
					paddingLeft: 4,
					paddingRight: 4,
				}),
				dropdownIndicator: (provided) => ({
					...provided,
					color: "#2B2830",
				}),
				clearIndicator: (provided) => ({
					...provided,
					color: "#2B2830",
				}),
				multiValue: (provided) => ({
					...provided,
					backgroundColor: "hsl(0, 0%, 93%)",
				}),
				multiValueLabel: (provided) => ({
					...provided,
					paddingLeft: 8,
				}),
				option: (provided, state) => ({
					...provided,
					color: "var(--color-text)",
					backgroundColor: state.isSelected
						? "var(--color-beige-light)"
						: provided.backgroundColor,
				}),
			}}
			theme={(theme) => ({
				...theme,
				borderRadius: 3,
				colors: {
					...theme.colors,
					primary25: "#e3f1d9",
					primary50: "#e3f1d9",
					primary75: "var(--color-beige-light)",
					primary: "#004249",
					neutral20: "#c1c1c1",
				},
				spacing: {
					...theme.spacing,
					controlHeight: 46,
				},
			})}
			{...props}
		/>
	);
};

type SelectOption = { value: number | string; label: string };

interface SelectProps {
	label: string;
	options: SelectOption[];
	selected: (number | string)[];
	setSelected: (value: (number | string)[]) => void;
}

/**
 * Single select
 */
export const Select = ({
	label,
	selected,
	setSelected,
	options,
	...reactSelectProps
}: SelectProps & StateManagerProps) => {
	if (!options || !options.length) {
		return null;
	}

	return (
		<div className="em-service-select">
			<div className="em-service-select__label input-label" aria-hidden="true">{label}</div>
			<AppReactSelect
				aria-label={label}
				isMulti={false}
				isClearable={true}
				value={options.filter((option) =>
					selected
						.map((each) => each.toString())
						.includes(option.value.toString())
				)}
				options={options}
				onChange={(uncertainValue) => {
					if (typeof uncertainValue === "object" && uncertainValue !== null) {
						const value = uncertainValue as SelectOption;
						setSelected([value.value]);
					} else {
						setSelected([]);
					}
				}}
				{...reactSelectProps}
			/>
		</div>
	);
};

/**
 * Button select
 */
interface ButtonSelectProps extends Omit<SelectProps, "placeholder"> {
	orientation?: "horizontal" | "vertical";
	showAllText?: string;
}

export const ButtonSelect = ({
	label,
	selected,
	setSelected,
	options,
	orientation = "horizontal",
	showAllText = "Show all",
}: ButtonSelectProps) => {
	if (!options || !options.length) {
		return null;
	}

	return (
		<div className="em-service-button-select">
			<div className="em-service-button-select__label input-label screen-reader-text">{label}</div>

			<div className="em-service-button-select__buttons" data-orientation={orientation}>
				<button
					className="em-service-button-select__button"
					disabled={selected?.length === 0}
					onClick={() => setSelected([])}
				>
					{showAllText}
				</button>

				{options.map(({ label, value }) => (
					<button
						key={value}
						className="em-service-button-select__button"
						disabled={selected.includes(value)}
						onClick={() => setSelected([value])}
					>
						{label}
					</button>
				))}
			</div>
		</div>
	);
};

/**
 * Icon button
 */
interface IconButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	label: string;
	icon: string;
}

export const IconButton = ({
	label,
	icon,
	...buttonProps
}: IconButtonProps) => {
	return (
		<button
			aria-label={label}
			className="em-icon-button"
			data-icon={icon}
			{...buttonProps}
		/>
	);
};
